import { useCallback, useEffect, useRef } from "react";

// Overlay params carry right-pane state (entity preview, document viewer),
// not list state. The preview writes ?entityId=... into the browser history
// entry via replaceState — outside react-router — so browser-back re-enters
// the list with params the scroll-time key never had. Stripping them keeps
// save and restore keyed to the same list state.
const OVERLAY_PARAMS = ["entityId", "factName", "docId"];

export function buildScrollStorageKey(
  pathname: string,
  search: string
): string {
  const params = new URLSearchParams(search);
  for (const name of OVERLAY_PARAMS) {
    params.delete(name);
  }
  const remaining = params.toString();
  return `rms-table-scroll:${pathname}${remaining ? `?${remaining}` : ""}`;
}

export interface TableScrollRestoreOptions {
  // sessionStorage key for the current list URL (pathname + search). Page,
  // filters, and sort already round-trip through the URL, so keying on it
  // scopes each saved offset to one exact list state (#10594).
  storageKey: string;
  // True once real rows are rendered. The restore must wait for them: the
  // skeleton/empty states render without the scroll container, and an empty
  // container clamps any assigned scrollTop back to 0.
  ready: boolean;
}

export interface TableScrollRestoreHandle {
  attachScrollElement: (el: HTMLDivElement | null) => void;
  resetToTop: () => void;
}

/**
 * Saves an RMS list's scroll offset per URL and restores it once per mount,
 * so navigating back to a list (in-app Back or browser back) returns the
 * user to where they left off (#10594).
 */
export function useTableScrollRestore({
  storageKey,
  ready,
}: TableScrollRestoreOptions): TableScrollRestoreHandle {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const keyRef = useRef<string>(storageKey);
  keyRef.current = storageKey;
  const readyRef = useRef<boolean>(ready);
  readyRef.current = ready;
  // Restoring more than once per mount would re-apply a stale offset after
  // an in-session page change, which must land at the top instead.
  const restoredRef = useRef<boolean>(false);

  const saveCurrentOffset = useCallback(() => {
    const el = elementRef.current;
    if (el) {
      sessionStorage.setItem(keyRef.current, String(el.scrollTop));
    }
  }, []);

  const restoreOnce = useCallback(() => {
    if (restoredRef.current) {
      return;
    }
    const el = elementRef.current;
    if (!el) {
      return;
    }
    restoredRef.current = true;
    const saved = Number(sessionStorage.getItem(keyRef.current));
    if (Number.isFinite(saved) && saved > 0) {
      el.scrollTop = saved;
    }
  }, []);

  const attachScrollElement = useCallback(
    (el: HTMLDivElement | null) => {
      elementRef.current?.removeEventListener("scroll", saveCurrentOffset);
      elementRef.current = el;
      if (el) {
        el.addEventListener("scroll", saveCurrentOffset, { passive: true });
        // A callback ref doesn't re-render, so the ready-effect may have
        // already run before the element existed; attach is the other edge
        // where both conditions can first hold.
        if (readyRef.current) {
          restoreOnce();
        }
      }
    },
    [saveCurrentOffset, restoreOnce]
  );

  useEffect(() => {
    if (ready) {
      restoreOnce();
    }
  });

  useEffect(() => {
    return () => {
      elementRef.current?.removeEventListener("scroll", saveCurrentOffset);
    };
  }, [saveCurrentOffset]);

  const resetToTop = useCallback(() => {
    restoredRef.current = true;
    const el = elementRef.current;
    if (el) {
      el.scrollTop = 0;
    }
    // No storage write: when a page change triggers this, the URL (and so
    // keyRef) still holds the PREVIOUS page's key for one more effect pass —
    // writing would wipe that page's saved offset. An absent entry already
    // means "start at top".
  }, []);

  return { attachScrollElement, resetToTop };
}
